import { useQuery } from '@tanstack/react-query';
import { Briefcase, Building2, KanbanSquare, Mail, User } from 'lucide-react';
import { Link, useSearchParams } from 'react-router';
import { Card, Empty, ErrorBox, PageHeader, SearchInput, Spinner } from '../components/ui';
import { dateTime } from '../lib/format';
import { supabase, unwrap } from '../lib/supabase';

const KINDS = [
  { kind: 'contact', label: 'People', icon: User, href: (id: string) => `/contacts/${id}` },
  { kind: 'organization', label: 'Companies', icon: Briefcase, href: (id: string) => `/companies/${id}` },
  { kind: 'building', label: 'Buildings', icon: Building2, href: (id: string) => `/buildings/${id}` },
  { kind: 'deal', label: 'Deals', icon: KanbanSquare, href: (id: string) => `/deals/${id}` },
  { kind: 'interaction', label: 'Correspondence & notes', icon: Mail, href: null },
] as const;

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const { data = [], isLoading, error } = useQuery({
    queryKey: ['search', q],
    enabled: q.trim().length > 1,
    queryFn: async () => {
      const hits = unwrap(await supabase.rpc('search_all', { q, max_results: 100 }));
      // Correspondence hits link to the person they belong to.
      const ids = hits.filter((h) => h.kind === 'interaction').map((h) => h.id);
      const owners = ids.length ? unwrap(await supabase.from('interactions').select('id, contact_id').in('id', ids)) : [];
      const contactOf = new Map(owners.map((o) => [o.id, o.contact_id]));
      return hits.map((h) => ({ ...h, contactId: contactOf.get(h.id) }));
    },
  });

  return (
    <div>
      <PageHeader title="Search" subtitle="People, companies (including legal names), buildings, deals, and every email, call and note." />
      <SearchInput value={q} onChange={(v) => setParams(v ? { q: v } : {})} placeholder="Try a name, a street, or a phrase like “ground floor”" className="mb-4 !max-w-xl" />
      <ErrorBox error={error} />
      {isLoading && q ? (
        <Spinner label="Searching…" />
      ) : q.trim().length < 2 ? null : data.length === 0 ? (
        <Empty>No matches for “{q}”.</Empty>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {KINDS.map((k) => {
            const hits = data.filter((h) => h.kind === k.kind);
            if (!hits.length) return null;
            return (
              <Card key={k.kind} title={`${k.label} (${hits.length})`}>
                <ul className="space-y-2 text-sm">
                  {hits.map((h) => {
                    const href = k.href ? k.href(h.id) : h.contactId ? `/contacts/${h.contactId}` : null;
                    const body = (
                      <>
                        <div className="flex items-center gap-2">
                          <k.icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                          <span className="font-medium text-slate-900">{h.title}</span>
                          {h.occurred_at && <span className="ml-auto text-xs text-slate-400">{dateTime(h.occurred_at)}</span>}
                        </div>
                        {h.detail && <div className="ml-5 truncate text-xs text-slate-500">{h.detail.replace(/_/g, ' ')}</div>}
                      </>
                    );
                    return (
                      <li key={h.id}>
                        {href ? (
                          <Link to={href} className="block rounded px-1 py-0.5 hover:bg-slate-50">
                            {body}
                          </Link>
                        ) : (
                          body
                        )}
                      </li>
                    );
                  })}
                </ul>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
